import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Status of a single uploaded raw Yardi export source table
 */
export type TYardiSourceFileStatus = {
    sourceTable: string;
    label: string;
    present: boolean;
    rowCount: number;
    uploadedAt: string | null;
    parseOk: boolean;
    parseStatus: "pending" | "succeeded" | "failed";
    parseError: string | null;
};

/**
 * Readiness of a derived settings tab (Books, CoA, Property Codes, Segments)
 */
export type TYardiTabStatus = {
    tab: string;
    requiredFiles: string[];
    missingFiles: string[];
    ready: boolean;
    generatedAt: string | null;
    isStale: boolean;
    blockedBy: string[];
    generationStatus: string;
    generationError: string | null;
    counts: Record<string, number> | null;
    /** Rows imported under a fallback value. Capped by the API; warningCount is the true total. */
    warnings: string[];
    warningCount: number;
    /** Required source tables with a parse in flight; generation is refused while non-empty. */
    parsingFiles: string[];
};

/**
 * Combined Yardi import status (source files + derived tabs)
 */
export type TYardiImportStatus = {
    files: TYardiSourceFileStatus[];
    tabs: TYardiTabStatus[];
};

/**
 * Progress of the bulk "revalidate all files" background run.
 * status: "idle" (never run) | "running" | "completed" | "failed".
 */
export type TYardiRevalidation = {
    status: string;
    total: number;
    processed: number;
    changed: number;
    failed: number;
    eligible: number;
};

/**
 * Yardi import slice state
 */
export type TYardiImportState = {
    yardiImportStatusState: TAsyncState<TYardiImportStatus>;
    yardiRevalidation: TYardiRevalidation;
};

/**
 * Yardi import slice actions
 */
export type TYardiImportActions = {
    setYardiImportLoading: () => void;
    setYardiImportStatus: (data: TYardiImportStatus) => void;
    setYardiImportError: (error: Error) => void;
    setYardiRevalidation: (data: TYardiRevalidation) => void;
    resetYardiImport: () => void;
};

/**
 * Complete Yardi import slice type
 */
export type TSliceYardiImport = TYardiImportState & TYardiImportActions;

const initialRevalidation: TYardiRevalidation = {
    status: "idle",
    total: 0,
    processed: 0,
    changed: 0,
    failed: 0,
    eligible: 0,
};

const initialState: TYardiImportState = {
    yardiImportStatusState: initialAsyncState(),
    yardiRevalidation: initialRevalidation,
};

/**
 * Creates the Yardi import slice for the store.
 * Tracks the status of raw Yardi export uploads and derived-tab readiness.
 */
export const createSliceYardiImport = (): TStateCreator<TSliceYardiImport> => {
    return (set) => ({
        ...initialState,

        setYardiImportLoading: () => {
            set((state) => ({
                ...state,
                yardiImportStatusState: pendingAsyncState(state.yardiImportStatusState.data),
            }));
        },

        setYardiImportStatus: (data: TYardiImportStatus) => {
            set({ yardiImportStatusState: fulfilledAsyncState(data) });
        },

        setYardiImportError: (error: Error) => {
            set((state) => ({
                ...state,
                yardiImportStatusState: rejectedAsyncState(
                    error,
                    state.yardiImportStatusState.data,
                ),
            }));
        },

        setYardiRevalidation: (data: TYardiRevalidation) => {
            set({ yardiRevalidation: data });
        },

        resetYardiImport: () => {
            set(initialState);
        },
    });
};
